import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { pillarArticles } from '@/mocks/pillarArticles';

type Lang = 'en' | 'pt' | 'de';

function normalizeLang(lng: string | undefined): Lang {
  const base = (lng || 'pt').split('-')[0];
  return base === 'en' || base === 'de' ? base : 'pt';
}

export default function PillarPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);

  const article = pillarArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 px-4 md:px-6 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">
            Article not found
          </h1>
          <Link to="/" className="text-primary-600 font-medium hover:text-primary-700 cursor-pointer">
            {t('article.backToHome')}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const title = article.title[lang];
  const summary = article.summary[lang];
  const intro = article.intro[lang];
  const takeaways = article.takeaways[lang];

  const schemaArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: summary,
    datePublished: '2026-08-01',
    dateModified: '2026-08-26',
    author: {
      '@type': 'Organization',
      name: 'CSSBuy Spreadsheet Resource',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CSSBuy Spreadsheet',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(schemaArticle)}</script>

      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-background-100 border-b border-background-200/70 mt-16">
        <div className="px-4 md:px-6 py-3">
          <nav className="max-w-4xl mx-auto">
            <ol className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
              <li>
                <Link to="/" className="hover:text-foreground-800 cursor-pointer">{t('article.backToHome')}</Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-xs" /></li>
              <li className="text-foreground-700 font-medium">{title}</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-background-200/70">
        <div className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest text-primary-600 uppercase mb-4">
              <i className={`${article.icon} text-base`} />
              {t('article.lastUpdated')}: August 2026
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4 leading-tight">
              {title}
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
              {summary}
            </p>
          </div>
        </div>
      </header>

      {/* Article Body */}
      <article className="py-10 md:py-16">
        <div className="px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Intro */}
            <div className="mb-12">
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                {intro}
              </p>
            </div>

            {/* Sections */}
            {article.sections.map((section, idx) => (
              <div key={idx} className="mb-12">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
                  {section.heading[lang]}
                </h2>
                <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
                  {section.body[lang]}
                </p>
              </div>
            ))}

            {/* Takeaways */}
            <div className="mb-12 bg-white rounded-xl border border-background-200/70 p-6 md:p-8">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                {t('article.takeaways')}
              </h2>
              <ul className="space-y-3">
                {takeaways.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <i className="ri-check-double-line text-primary-500 mt-0.5 flex-shrink-0" />
                    <span className="text-foreground-600 text-sm md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Internal Link to Home */}
            <div className="mb-12 bg-primary-50 rounded-xl border border-primary-200/50 p-6">
              <div className="flex items-start gap-3">
                <i className="ri-home-line text-primary-600 text-xl mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-primary-900 mb-1">{t('article.backToHome')}</h3>
                  <p className="text-sm text-primary-700 mb-2">
                    {t('article.backToHomeDesc')}
                  </p>
                  <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer">
                    <i className="ri-arrow-left-line" />
                    {t('article.backToHome')}
                  </Link>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-primary-500 rounded-xl p-8 md:p-10 text-center">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-3">
                {t('article.ctaTitle')}
              </h3>
              <p className="text-white/85 text-sm md:text-base mb-6">
                {t('article.ctaDesc')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 bg-white text-primary-600 font-medium px-8 py-3 rounded-lg hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('article.ctaPrimary')}
                  <i className="ri-arrow-right-line" />
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 bg-white/15 text-white font-medium px-8 py-3 rounded-lg hover:bg-white/25 backdrop-blur-sm transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('article.ctaSecondary')}
                  <i className="ri-shopping-cart-line" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Disclaimer */}
      <div className="bg-background-100 border-t border-background-200/70 py-4">
        <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
          <p className="text-xs text-foreground-500">
            {t('article.disclaimer')}
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}